// profile/PastEvents.jsx
import React, { useMemo } from 'react';
import axios from 'axios';
import EventList from '../EventList';
import useCachedFetch from './hooks/useCachedFetch';
import { useTranslation } from 'react-i18next';


const isCancelledStatus = (status) => {
  const value = String(status || 'none');
  return [
    'cancellation_pending_payment',
    'canceled',
    'refunding',
    'cancellation_completed',
    'cancellation_failed',
  ].includes(value);
};

const PastEvents = ({ user, isOwner }) => {
  const isBuyer = user?.role === 'buyer';
  const { t } = useTranslation('profile');

  // buyer: /my-tickets
  const ticketsQuery = useCachedFetch(
    `mytickets:${user?.id}`,
    async (signal) => {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/my-tickets`, { signal });
      return res.data;
    },
    { enabled: !!user?.id && isBuyer, ttlMs: 60_000 }
  );

  // seller: /users/:id/events/past
  const pastQuery = useCachedFetch(
    `past:${user?.id}`,
    async (signal) => {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/users/${user.id}/events/past`, { signal });
      return res.data;
    },
    { enabled: !!user?.id && !isBuyer, ttlMs: 60_000 }
  );

  const loading = isBuyer ? ticketsQuery.loading : pastQuery.loading;
  const error = isBuyer ? ticketsQuery.error : pastQuery.error;

  const events = useMemo(() => {
    if (!user?.id) return [];

    // buyer: ticketsから算出
    if (isBuyer) {
      const tickets = ticketsQuery.data?.tickets || [];
      const now = new Date();

      return (Array.isArray(tickets) ? tickets : [])
        .map((t) => t?.event)
        .filter((e) => {
          if (!e) return false;
          if (e?.status === 'draft') return false;
          if (isCancelledStatus(e?.cancellation_status)) return false;
          const base = e.end_date || e.date;
          if (!base) return false;
          const s = String(base).trim();
          const normalized = /^\d{4}-\d{2}-\d{2}$/.test(s) ? `${s}T23:59:59` : s.replace(' ', 'T');
          const endOfDay = new Date(normalized);
          return endOfDay < now;
        })
        .filter((ev, idx, arr) => arr.findIndex((x) => x && x.id === ev.id) === idx)
        .sort((a, b) => {
          const ba = b.end_date || b.date;
          const aa = a.end_date || a.date;
          const bn = /^\d{4}-\d{2}-\d{2}$/.test(String(ba)) ? new Date(`${ba}T23:59:59`) : new Date(ba);
          const an = /^\d{4}-\d{2}-\d{2}$/.test(String(aa)) ? new Date(`${aa}T23:59:59`) : new Date(aa);
          return bn - an;
        });
    }

    // seller: API結果をそのまま
    return Array.isArray(pastQuery.data) ? pastQuery.data : [];
  }, [isBuyer, pastQuery.data, ticketsQuery.data, user?.id]);

  if (loading) return <div>{t('loading')}</div>;
  if (error) return <div>{t('loadFailed')}</div>;
  if (!events.length) {
    return <div>{t('noEvents')}</div>;
  }

  return (
    <div>
      <EventList events={events} allowEdit={false} showMemoriesButton={Boolean(isOwner)} />
    </div>
  );
};

export default PastEvents;
